/**
 * CalcResults — renders a CalcResponse from any of the six calculators.
 *
 * This is the product surface: it has to read as defensible. It branches on
 * `pricing_mode`:
 *   - "priced" (commercial trio: crane/scaffold/lift) shows the rate range,
 *     source badges, and estimated totals — the pricing IS the product.
 *   - "quantities-only" (SEO trio: drywall/water/debris) shows a compact
 *     QTY / UNIT / CODE / ACTION / DESCRIPTION table that fits without a
 *     horizontal scroll, with NO rates and NO totals. Every line resolves to a
 *     real Xactimate item; Xactimate prices it at import.
 *
 * Layout, top to bottom: sample-rate banner (if mock) -> summary -> warnings ->
 * line-item table -> justification narrative -> assumptions -> disclaimers ->
 * copy button. When `beyond_tool_range` is true the tool is outside its
 * supported envelope, so the narrative becomes the primary message and the
 * table is suppressed.
 */
import { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import type { CalcResponse, CalcLineItem } from './calcApi';

function money(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function dollars(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

// Backend RateBasis slugs -> human labels for the no-rate chip.
const RATE_BASIS_LABEL: Record<string, string> = {
  'quote-locally': 'Quote locally',
  'jurisdiction-specific': 'Jurisdiction fee',
  'nearest-class': 'Nearest class',
  'estimating-heuristic': 'Estimating heuristic',
  cited: 'Cited rate',
  MOCK: 'Sample rate',
};

function basisLabel(basis: string): string {
  return RATE_BASIS_LABEL[basis] || basis || 'Quote locally';
}

/**
 * Rate cell content. Quantity-only line items have null rates (price at import
 * / quote locally): show the rate_basis as a chip rather than a bogus $0 range.
 *
 * B-670: when a line carries a published market band, the market range is the
 * headline number and the cited O&O rate demotes to a "cost floor" footnote.
 * The two are never blended.
 */
function RateCell({ item }: { item: CalcLineItem }) {
  const floor =
    item.rate_low != null && item.rate_high != null ? (
      <span className="tabular-nums">
        {dollars(item.rate_low)}
        {item.rate_high !== item.rate_low && <> to {dollars(item.rate_high)}</>}{' '}
        / {item.rate_unit}
      </span>
    ) : null;

  if (item.market_low != null && item.market_high != null) {
    return (
      <div className="space-y-0.5">
        <div className="font-medium tabular-nums text-foreground">
          {dollars(item.market_low)}
          {item.market_high !== item.market_low && (
            <> to {dollars(item.market_high)}</>
          )}{' '}
          / {item.market_unit}
          <span className="ml-1.5 rounded border border-border bg-muted/40 px-1 py-0.5 text-xs font-normal text-muted-foreground">
            market
          </span>
        </div>
        {floor && (
          <div className="text-xs text-muted-foreground">
            cost floor {floor}
          </div>
        )}
        {item.market_note && (
          <div className="text-xs text-muted-foreground">
            {item.market_note}
          </div>
        )}
      </div>
    );
  }

  if (!floor) {
    return (
      <span className="inline-block rounded border border-border bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground">
        {basisLabel(item.rate_basis)}
      </span>
    );
  }
  return (
    <div className="space-y-0.5">
      {floor}
      {item.market_covered && (
        <div className="text-xs text-muted-foreground">
          included in the operated market rate above
        </div>
      )}
    </div>
  );
}

/**
 * Xactimate code cell. Every quantities-only line resolves to a real
 * (cat, sel); render it as a chip (e.g. `DRY 1/2-`, `WTR DRYN4`, `FNC BRS`).
 * Only when a line genuinely has no code do we fall back to the "bid item"
 * chip — that is the exceptional case, not the default.
 */
function CodeChip({ item }: { item: CalcLineItem }) {
  const code = [item.cat, item.sel].filter(Boolean).join(' ');
  if (code) {
    return (
      <span className="inline-block whitespace-nowrap rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-xs text-foreground">
        {code}
      </span>
    );
  }
  return (
    <span className="inline-block whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
      bid item
    </span>
  );
}

/**
 * Compact quantities-only line-item table (SEO trio). Columns:
 * QTY / UNIT / CODE / ACTION / DESCRIPTION — sized to fit without a horizontal
 * scroll. No rates, no source badges, no totals: Xactimate prices at import.
 */
function QuantitiesTable({ lineItems }: { lineItems: CalcLineItem[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full table-auto text-left text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-2 py-2 font-medium">Qty</th>
            <th className="px-2 py-2 font-medium">Unit</th>
            <th className="px-2 py-2 font-medium">Code</th>
            <th className="px-2 py-2 font-medium">Action</th>
            <th className="px-2 py-2 font-medium">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {lineItems.map((item, i) => (
            <tr key={i} className="align-top">
              <td className="px-2 py-3 tabular-nums">{item.qty}</td>
              <td className="px-2 py-3 text-muted-foreground">{item.unit}</td>
              <td className="px-2 py-3">
                <CodeChip item={item} />
              </td>
              <td className="px-2 py-3 text-muted-foreground">
                {item.action || ''}
              </td>
              <td className="px-2 py-3">
                <div className="font-medium text-foreground">
                  {item.description}
                </div>
                {item.notes && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {item.notes}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Reason: clipboard can be blocked (insecure context / permissions).
      // Fail quietly rather than throw in the user's face.
      setCopied(false);
    }
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={copy}
      Icon={copied ? Check : Copy}
    >
      {copied ? 'Copied' : 'Copy for estimate'}
    </Button>
  );
}

export default function CalcResults({ result }: { result: CalcResponse }) {
  const {
    summary,
    line_items,
    narrative,
    assumptions,
    disclaimers,
    warnings,
    beyond_tool_range,
    using_mock_rates,
    pricing_mode,
    total_low,
    total_high,
    adjusted_total_low,
    adjusted_total_high,
    market_total_low,
    market_total_high,
    adjusted_market_total_low,
    adjusted_market_total_high,
    copy_text,
  } = result;

  const quantitiesOnly = pricing_mode === 'quantities-only';

  // total_low/high are computed sums of PRICED lines only. Quantities-only
  // packages (drywall, debris, water) never publish totals — we deliberately
  // do not show Xactimate pricing on those pages.
  const hasTotals =
    !quantitiesOnly &&
    total_low != null &&
    total_high != null &&
    total_high > 0;
  const showAdjusted =
    adjusted_total_low != null &&
    adjusted_total_high != null &&
    adjusted_total_high > 0;
  // B-670: when the package carries market bands, the market total is the
  // headline "expect to pay" number; the O&O total demotes to a footnote.
  const hasMarketTotals =
    !quantitiesOnly &&
    market_total_low != null &&
    market_total_high != null &&
    market_total_high > 0;
  const showMarketAdjusted =
    adjusted_market_total_low != null &&
    adjusted_market_total_high != null &&
    adjusted_market_total_high > 0;

  return (
    <div className="space-y-6">
      {using_mock_rates && (
        <div
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive"
        >
          SAMPLE RATES — rate ingestion pending, do not use for estimating.
        </div>
      )}

      {/* Summary line */}
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-lg font-semibold text-foreground">{summary}</p>
        {!beyond_tool_range && hasMarketTotals && (
          <>
            <p className="mt-1 text-base font-semibold text-foreground">
              Expect to pay {money(market_total_low!)} to{' '}
              {money(market_total_high!)}
              <span className="ml-1.5 align-middle rounded border border-border bg-muted/40 px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                published market rates
              </span>
              {showMarketAdjusted && (
                <span className="font-normal text-muted-foreground">
                  {' '}
                  (adjusted for your market:{' '}
                  {money(adjusted_market_total_low!)} to{' '}
                  {money(adjusted_market_total_high!)})
                </span>
              )}
            </p>
            {hasTotals && (
              <p className="mt-1 text-xs text-muted-foreground">
                Owner&apos;s cost floor {money(total_low!)} to{' '}
                {money(total_high!)} — ownership + operating schedules
                (USACE/Caltrans), shown as the justification anchor, not a
                market quote.
              </p>
            )}
          </>
        )}
        {!beyond_tool_range && !hasMarketTotals && hasTotals && (
          <p className="mt-1 text-sm text-muted-foreground">
            Estimated total {money(total_low!)} to {money(total_high!)}
            {showAdjusted && (
              <>
                {' '}
                (adjusted for your market: {money(adjusted_total_low!)} to{' '}
                {money(adjusted_total_high!)})
              </>
            )}
          </p>
        )}
        {!beyond_tool_range && !hasTotals && (
          <p className="mt-1 text-sm text-muted-foreground">
            {quantitiesOnly
              ? 'Every line is a verified Xactimate item. Quantities and codes below; Xactimate prices them at import.'
              : 'Quantities below. Rates are priced at import or quoted locally.'}
          </p>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map((w, i) => (
            <div
              key={i}
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {beyond_tool_range ? (
        // Outside the supported envelope: narrative is the whole answer.
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-2 text-base font-semibold">What to do next</h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
            {narrative}
          </p>
        </div>
      ) : (
        <>
          {/* Line-item table */}
          {quantitiesOnly ? (
            <QuantitiesTable lineItems={line_items} />
          ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Qty</th>
                  <th className="px-3 py-2 font-medium">Unit</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium">Rate range</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {line_items.map((item, i) => (
                  <tr key={i} className="align-top">
                    <td className="px-3 py-3 tabular-nums">{item.qty}</td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {item.unit}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-foreground">
                        {item.description}
                        {item.is_bid_item && (
                          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                            bid item
                          </span>
                        )}
                      </div>
                      {(item.cat || item.sel) && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {[item.cat, item.sel].filter(Boolean).join(' ')}
                        </div>
                      )}
                      {item.notes && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {item.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <RateCell item={item} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        {[...(item.market_sources ?? []), ...item.sources].map((s, j) => {
                          const badge = (
                            <span className="inline-block rounded border border-border bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground">
                              {s.source}
                              {s.edition_date ? ` · ${s.edition_date}` : ''}
                            </span>
                          );
                          return s.url ? (
                            <a
                              key={j}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:opacity-80"
                              title={`${s.doc} (${s.region_basis})`}
                            >
                              {badge}
                            </a>
                          ) : (
                            <span key={j} title={`${s.doc} (${s.region_basis})`}>
                              {badge}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Justification narrative */}
          {narrative && (
            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-2 text-base font-semibold">
                Justification narrative
              </h2>
              <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
                {narrative}
              </p>
            </div>
          )}
        </>
      )}

      {/* Assumptions */}
      {assumptions.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 text-base font-semibold">Assumptions</h2>
          <dl className="grid gap-2 sm:grid-cols-2">
            {assumptions.map((a, i) => (
              <div key={i} className="text-sm">
                <dt className="font-medium text-foreground">{a.key}</dt>
                <dd className="text-muted-foreground">
                  {a.value}
                  {a.basis && (
                    <span className="text-xs"> — {a.basis}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Copy + disclaimers */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CopyButton text={copy_text} />
      </div>

      {disclaimers.length > 0 && (
        <div className="space-y-1 text-xs text-muted-foreground">
          {disclaimers.map((d, i) => (
            <p key={i}>{d}</p>
          ))}
        </div>
      )}
    </div>
  );
}
